import { generatePath, useNavigate } from 'react-router-dom';

import {
  ActivityBuilderButtonSkeleton,
  ActivityBuilderHeaderContainer,
  ActivityBuilderSecondaryHeaderButtons,
} from './activity-builder.style';
import SimpleHeader from 'approot/shared/layout/simple/simple-header';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/buttons';
import * as Styled from './activity-builder-header.style';
import {
  ACTIVITY_BUILDER_DRAWER_HISTORY,
  ACTIVITY_BUILDER_TOOLBAR_ID,
  ACTIVITY_BUILDER_DRAWER_INLINE_BREAKPOINT,
  ACTIVITY_BUILDER_DRAWER_TEACHING_NOTES,
  ACTIVITY_BUILDER_DRAWER_GLOBAL_SETTINGS,
  ACTIVITY_BUILDER_GLOBAL_ADD,
} from './activity-builder.constants';
import { useActivityBuilderContext } from './activity-builder.context';
import { useActivityBuilderApollo } from './activity-builder.apollo';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { useMediaQuery } from 'approot/shared/style';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { DropdownKebab } from 'approot/shared/dropdown/dropdown-kebab';
import {
  DropdownItem,
  DropdownLink,
} from 'approot/shared/dropdown/dropdown-item.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { ComponentsAddMenu } from './components/components-menu/components-add-menu';
import IconPlusInCircle from 'approot/shared/icons/icon-plus-in-circle';
import { getLastSortValue, sortComponents } from './activity-builder.utils';
import { getPresentationUrl } from 'approot/activity-builder/activity-builder-url.utils';
import {
  useComponentMutations,
  useGetInteractiveLessonParams,
} from './activity-builder.hooks';
import type { InteractiveLessonSectionFragment } from './lesson-builder.graphql.generated';
import ActivityBuilderZoomToFitControl from './zoom-to-fit/activity-builder-zoom-to-fit-control';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { useActivityBuilderRequest } from './activity-builder-request.apollo';
import IconRefresh from 'approot/shared/icons/icon-refresh';
import IconTick from 'approot/shared/icons/icon-tick';
import type { GetCurrentActivitiesEditorsSubscription } from './lesson-builder.graphql.generated';
import ActiveEditorsDisplay from './active-editors/active-editors-display';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  showSkeleton: boolean;
  contentSlug: string;
  sectionId?: string;
  components: Pluck<InteractiveLessonSectionFragment, 'components'>[];
  isDraftMode?: boolean;
  currentActivitiesEditors?: Pluck<
    GetCurrentActivitiesEditorsSubscription,
    'getCurrentActivitiesEditors'
  >[];
  onSaveAndExitClick?: () => void;
};

const ActivityBuilderHeader = ({
  showSkeleton,
  contentSlug,
  sectionId,
  components,
  currentActivitiesEditors,
  isDraftMode,
  onSaveAndExitClick,
}: Props) => {
  const isMediaUpSmall = useMediaQuery(
    `(min-width: ${ACTIVITY_BUILDER_DRAWER_INLINE_BREAKPOINT}px)`
  );
  const { showSuccess } = useSnackbar();
  const navigate = useNavigate();
  const { slug, userClassId, contentType } = useGetInteractiveLessonParams();

  const {
    isDrawerActive,
    isZoomToFit,
    activeComponent,
    openDrawer,
    setZoomToFit,
    setSlideScaleRatio,
  } = useActivityBuilderContext();
  const {
    data: { activeSectionId },
  } = useActivityBuilderApollo();

  const { hasInFlightRequest } = useActivityBuilderRequest();
  const { handleAdd } = useComponentMutations({
    sectionId: sectionId!,
  });

  if (showSkeleton)
    return <SimpleHeader headerMenu={<ActivityBuilderButtonSkeleton />} />;

  return (
    <>
      <SimpleHeader
        logoLink={getUserClassPath(`${generatePath(contentSlug)}/edit`)}
        onLogoClick={onSaveAndExitClick}
        headerMenu={
          <ActivityBuilderHeaderContainer>
            <ActivityBuilderSecondaryHeaderButtons>
              <ActiveEditorsDisplay editors={currentActivitiesEditors || []} />
              <Styled.ActivityBuilderUpdateStatus
                $isUpdating={hasInFlightRequest}
              >
                {hasInFlightRequest ? (
                  <>
                    <IconRefresh width={16} />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <IconTick width={16} height={12} />
                    <span>Saved</span>
                  </>
                )}
              </Styled.ActivityBuilderUpdateStatus>
              {isMediaUpSmall ? (
                <>
                  <ActivityBuilderZoomToFitControl
                    isZoomToFit={isZoomToFit}
                    aria-label="Editor view mode"
                    data-selector="lb-zoom-to-fit-btn"
                    onClick={zoomToFit => {
                      if (isZoomToFit !== zoomToFit) {
                        setZoomToFit(zoomToFit);
                        showSuccess(
                          `Slide layout changed to ${
                            zoomToFit ? 'presentation' : 'mobile'
                          }`
                        );
                        if (!zoomToFit) {
                          setSlideScaleRatio(1);
                        }
                      }
                    }}
                  />
                  <DraftHistoryButton openDrawer={openDrawer} />
                  <PrimaryButton
                    data-selector="lb-present-btn"
                    type="button"
                    onClick={() => {
                      navigate(
                        getPresentationUrl({
                          slug,
                          userClassId,
                          sectionId: activeSectionId,
                          contentType,
                        })
                      );
                    }}
                    disabled={isDraftMode}
                  >
                    Present
                  </PrimaryButton>
                </>
              ) : (
                <>
                  {isDraftMode ? (
                    <DraftHistoryButton openDrawer={openDrawer} />
                  ) : (
                    <>
                      <ComponentsAddMenu
                        canRenderMenu={
                          activeComponent?.id === ACTIVITY_BUILDER_GLOBAL_ADD
                        }
                        render={onClick => (
                          <SecondaryButtonIcon
                            type="button"
                            $isActive={false}
                            aria-label="Click to add a new component"
                            aria-haspopup="dialog"
                            onClick={onClick}
                          >
                            <IconPlusInCircle width={20} height={20} />
                          </SecondaryButtonIcon>
                        )}
                        addComponent={props => {
                          handleAdd({
                            ...props,
                            sort: getLastSortValue(sortComponents(components)),
                          });
                        }}
                      />
                      <DropdownKebab
                        borderRadius={8}
                        menuWidth={190}
                        dropdownContents={
                          <>
                            <DropdownLink
                              data-dropdown-item="true"
                              to={getPresentationUrl({
                                slug,
                                userClassId,
                                sectionId: activeSectionId,
                                contentType,
                              })}
                            >
                              Present
                            </DropdownLink>
                            <DropdownItem
                              onClick={() => {
                                openDrawer({
                                  componentId:
                                    ACTIVITY_BUILDER_DRAWER_TEACHING_NOTES,
                                });
                              }}
                            >
                              Teaching Notes
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                openDrawer({
                                  componentId:
                                    ACTIVITY_BUILDER_DRAWER_GLOBAL_SETTINGS,
                                });
                              }}
                            >
                              Settings
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                openDrawer({
                                  componentId: ACTIVITY_BUILDER_DRAWER_HISTORY,
                                });
                              }}
                            >
                              Draft History
                            </DropdownItem>
                          </>
                        }
                        aria-label="Click to view all builder action buttons"
                      />
                    </>
                  )}
                </>
              )}
            </ActivityBuilderSecondaryHeaderButtons>
          </ActivityBuilderHeaderContainer>
        }
      />
      <Styled.HeaderToolbar $isActive={isDrawerActive}>
        <Styled.HeaderToolbarPlaceholder id={ACTIVITY_BUILDER_TOOLBAR_ID} />
      </Styled.HeaderToolbar>
    </>
  );
};

const DraftHistoryButton = ({
  openDrawer,
}: {
  openDrawer: (props: { componentId: string }) => void;
}) => (
  <TooltipWrapper hoverText="Draft History">
    <SecondaryButtonIcon
      type="button"
      onClick={() => {
        openDrawer({
          componentId: ACTIVITY_BUILDER_DRAWER_HISTORY,
        });
      }}
      aria-label="Click to view this lesson's draft history"
      data-selector="btn-draft-history"
    >
      <ActivityBuilderInlineSvg id="icon_history" width={20} height={20} />
    </SecondaryButtonIcon>
  </TooltipWrapper>
);

export default ActivityBuilderHeader;
